import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { KaneaLogo } from '../../components/Common/KaneaLogo';
import { API_URL } from '../../config';
import AuthBrandCanvas from '../../components/Auth/AuthBrandCanvas';
import usePageTitle from '../../hooks/usePageTitle';

// styles
import '../../styles/auth.css';

const VerifyEmailScreen: React.FC = () => {
  usePageTitle('Vérification d\'email — KaneaBourse');
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Token de vérification manquant ou invalide.');
        return;
      }

      try {
        const response = await fetch(`${API_URL}/auth/verify-email?token=${token}`);
        const data = await response.json();

        if (response.ok) {
          setStatus('success');
          setMessage(data.message || 'Votre adresse email a été vérifiée avec succès !');
        } else {
          setStatus('error');
          setMessage(data.detail || 'Lien de vérification invalide ou expiré.');
        }
      } catch (error) {
        setStatus('error');
        setMessage('Une erreur réseau est survenue. Veuillez réessayer.');
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="auth-root">
      <div className="auth-panel">

        {/* ── Panneau gauche branding ── */}
        <div className="auth-brand-panel">
          <AuthBrandCanvas />
          <div className="auth-brand-spotlight" />

          <div className="auth-brand-logo">
            <div className="auth-brand-logo-img-wrap">
              <KaneaLogo variant="mark" title={null} />
            </div>
            <span className="auth-brand-name">KaneaBourse</span>
            <div className="auth-brand-logo-dot" />
          </div>

          <div className="auth-brand-body">
            <h2>
              N'investissez plus sur des <em>rumeurs</em>
            </h2>
            <p>
              KaneaBourse analyse froidement les bilans officiels BRVM —
              14 modèles de valorisation, indicateurs techniques, Nia votre copilote en français.
              Analysez ici, passez vos ordres chez votre SGI.
            </p>
            <div className="auth-brand-stats">
              <div>
                <div className="auth-brand-stat-value">10s</div>
                <div className="auth-brand-stat-label">Pour décider</div>
              </div>
              <div>
                <div className="auth-brand-stat-value">48</div>
                <div className="auth-brand-stat-label">Sociétés suivies</div>
              </div>
              <div>
                <div className="auth-brand-stat-value">24/7</div>
                <div className="auth-brand-stat-label">Copilote Nia</div>
              </div>
            </div>
          </div>

          <div className="auth-brand-footer">
            © {new Date().getFullYear()} KaneaBourse · Données EOD BRVM
          </div>
        </div>

        {/* ── Panneau droit formulaire ── */}
        <div className="auth-form-panel">
          <div className="auth-form-inner">

            {/* ── Logo Mobile ── */}
            <div className="auth-mobile-logo">
              <KaneaLogo variant="mark" title={null} />
              <span className="auth-mobile-logo-text">KaneaBourse</span>
            </div>

            {status === 'loading' && (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <div style={{ fontSize: '48px', marginBottom: '20px', animation: 'spin 2s linear infinite' }}>⏳</div>
                <div className="auth-form-header">
                  <h1>Vérification en cours…</h1>
                  <p>Veuillez patienter pendant que nous validons votre adresse email.</p>
                </div>
              </div>
            )}

            {status === 'success' && (
              <div>
                <div className="auth-form-header">
                  <h1>Email vérifié ! 🎉</h1>
                  <p>Félicitations, votre compte est désormais actif et prêt à être utilisé.</p>
                </div>

                <div className="auth-success" style={{
                  padding: '16px',
                  backgroundColor: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  borderRadius: '8px',
                  color: '#34d399',
                  fontSize: '0.9rem',
                  margin: '24px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <span style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>✓</span> {message}
                </div>

                <Link to="/login" className="auth-btn-primary" style={{ display: 'block', textAlign: 'center' }}>
                  Se connecter à mon compte →
                </Link>
              </div>
            )}

            {status === 'error' && (
              <div>
                <div className="auth-form-header">
                  <h1>Vérification échouée ❌</h1>
                  <p>Le lien de vérification a expiré, est incomplet ou a déjà été utilisé.</p>
                </div>

                <div className="auth-error" style={{ margin: '24px 0' }}>
                  <span>⚠</span> {message}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <Link to="/login" className="auth-btn-primary" style={{ textAlign: 'center' }}>
                    Retour à la connexion
                  </Link>
                  <Link to="/members/signup" className="auth-btn-secondary" style={{ textAlign: 'center' }}>
                    Créer un nouveau compte
                  </Link>
                </div>
              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};

export default VerifyEmailScreen;
